/**
 * 單據附件卡片(REQ-20260907002)—— **跨單據共用的一顆元件**。
 *
 * 需求原文:「這是共用元件。材料類、外發加工類的表單直接套同一個,不要各寫一份。」
 * 所以它只吃 `docType` + `docId`,不知道自己掛在哪一種單上 ——
 * 之後出貨單 / 報價單要貼檔案,掛上去就有。
 *
 * 三條需求原文明講、實作時很容易被違反的規則:
 *
 *  1. **無條件渲染**。不看付款方式、不看進貨類別 ——「任何進貨單都要能貼單據」。
 *     連建單模式(單還不存在、presign 拿不到 id)都照樣渲染:
 *      - 父層有傳 `pendingFiles` + `onPendingFilesChange`(REQ-20261012001)→
 *        拍照 / 選檔案照樣能按,選到的檔案**先不上傳**、交給父層暫存,
 *        父層在建單成功拿到 id 之後才補傳;
 *      - 沒傳 → 上傳鈕停用並說明原因(改動前的行為,DOM 逐字不變)。
 *
 *     ⚠️ 前一種**推翻了 REQ-20260907002「不為此改建單流程」的結論** —— 那次的理由是
 *     「presign 需要 docId,建單前拿不到」,而這次使用者明確要求「建立草稿時也可以
 *     順便上傳」。是需求變了,不是當初漏看。presign 需要 docId 這件事沒變,
 *     變的是上傳的**時間點**:從選檔當下延到建單之後。
 *  2. **類型是可選標籤不是必填**。沒選類型照樣傳得上去,chip 顯示「未分類」,
 *     事後點 chip 補(走 PATCH)、也清得掉。強制先選會拖慢貼單據的動作。
 *  3. **接受 `image/*` 與 `application/pdf`**。PDF 縮圖畫第一頁;
 *     畫不出來就退回檔案圖示 —— 縮圖失敗**絕不能讓表單壞掉**。
 *
 * 已送出確認 / 已過帳的單:卡片**仍然顯示、附件仍然看得到與預覽得到**,
 * 只有上傳與刪除停用(`disabledReason`)。這是既有的 R-12 + R-08 凍結規則,
 * 本次沒有放寬(見 REQ-20260907002 的待確認事項)。
 *
 * 刪除沿用附件區既有的二次確認做法(`window.confirm`,REQ-20260819007),
 * 不自創新彈窗。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DOCUMENT_ATTACHMENT_ACCEPT,
  DOCUMENT_ATTACHMENT_CATEGORIES,
  DOCUMENT_ATTACHMENT_CATEGORY_KEYS,
  type AttachmentRotation,
  type DocumentAttachmentCategory,
  type DocumentAttachmentResponse,
  type DocumentType,
} from '@app/shared';
import { ImageLightbox, type LightboxImage } from '../features/attachments/ImageLightbox';
import {
  deleteDocumentAttachment,
  getDocumentAttachmentUrl,
  patchDocumentAttachmentCategory,
  saveDocumentAttachmentRotation,
  uploadDocumentAttachment,
  useDocumentAttachmentUrl,
  useDocumentAttachments,
} from '../features/document-attachments/api';

interface Props {
  docType: DocumentType;
  /**
   * 單還不存在時(建單模式)傳 undefined —— 卡片照樣渲染;上傳鈕停用,
   * 除非父層有接 `pendingFiles`(見下方)
   */
  docId: number | undefined;
  /**
   * 停用上傳 / 刪除的原因。有值時整張卡片變唯讀(但**看得到也預覽得到**)。
   * 沒傳 + `docId` 有值 = 可編輯。
   */
  disabledReason?: string | undefined;
  /** 覆寫標題右邊的副標。不傳時是進貨單的那一句 */
  subtitle?: string;
  /** 卡片標題。不傳時是「單據附件」(REQ-20260917001 的工單附件傳「工單附件」) */
  title?: string;
  /**
   * `data-testid` 的前綴(REQ-20260917001)。同一張表單掛兩張卡片時,
   * 兩張的 card / count / camera-input / file-input 會撞在一起。
   *
   * ⚠️ **預設值一個字都不能改** —— 既有測試用 `document-attachment-card` /
   * `-count` / `-file-input` 定位,不傳新 prop 時 DOM 必須逐字與改動前相同
   * (紅線同 REQ-20260907002 §2.10 的 `inputId` / `bare`)。
   */
  testIdPrefix?: string;
  /**
   * 建單模式的暫存檔(REQ-20261012001)。**兩個一起傳、而且 `docId` 是 undefined**
   * 才生效:選到的檔案不上傳,呼叫 `onPendingFilesChange` 交給父層;卡片逐列列出
   * 「待上傳」,✕ 只是從陣列拿掉。上傳由父層在建單成功拿到 id 之後做 ——
   * 卡片拿不到 id,也不該知道建單流程。
   *
   * 沒傳時行為與 DOM 逐字與改動前相同(停用 + 「建立草稿後即可上傳」)。
   */
  pendingFiles?: File[];
  onPendingFilesChange?: (files: File[]) => void;
}

/** 建單模式(還沒有 docId)、父層又沒接暫存檔時上傳鈕的說明 */
const NO_DOC_REASON = '建立草稿後即可上傳';

export function DocumentAttachmentCard({
  docType,
  docId,
  disabledReason,
  subtitle = '廠商給的對帳單、發票、簽收單',
  title = '單據附件',
  testIdPrefix = 'document-attachment',
  pendingFiles,
  onPendingFilesChange,
}: Props): JSX.Element {
  const listQuery = useDocumentAttachments(docType, docId);
  const attachments = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);

  /** 建單模式而且父層接得住暫存檔(REQ-20261012001)—— 選檔照收,先不上傳 */
  const pending =
    docId == null && pendingFiles && onPendingFilesChange
      ? { files: pendingFiles, onChange: onPendingFilesChange }
      : null;
  const reason = docId == null && !pending ? NO_DOC_REASON : disabledReason;
  const canEdit = reason == null;

  // lightbox 的狀態放在清單這一層,不是每張縮圖各存一份 ——
  // 同一張單的多張圖片在放大檢視裡才能左右切換(沿用 REQ-20260901011)
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const imageAttachments = useMemo(
    () => attachments.filter((a) => a.mimeType.startsWith('image/')),
    [attachments],
  );
  const lightboxImages = useMemo<LightboxImage[]>(
    () =>
      imageAttachments.map((a) => ({
        uuid: a.uuid,
        originalName: a.originalName,
        // 已存的檢視角度(REQ-20260909004),放大檢視開起來就是正的
        rotation: a.rotation,
      })),
    [imageAttachments],
  );
  /**
   * lightbox 自備取號(沿用 REQ-20260901011 的 `resolveUrl`)—— 單據附件的
   * signed URL 在另一組端點。**共用 lightbox 的預設行為一個字都沒改**。
   * 包 `useCallback` 是為了參照穩定,理由見 ImageLightbox 裡 resolveUrlRef 的註解。
   */
  const resolveAttachmentUrl = useCallback(
    async (image: LightboxImage): Promise<string> => {
      const found = imageAttachments.find((a) => a.uuid === image.uuid);
      if (!found || docId == null) throw new Error('找不到附件');
      return getDocumentAttachmentUrl(docType, docId, found.id);
    },
    [imageAttachments, docType, docId],
  );

  /**
   * 上傳 / 改分類 / 刪除之後重取清單。**用這支 query 自己的 `refetch()`**,
   * 不透過 `useQueryClient()` —— 這顆是共用元件,不該要求每個掛它的地方
   * (包含測試)都先包一層 `QueryClientProvider`;要重取的也就只有這一支查詢。
   */
  const refresh = (): void => {
    void listQuery.refetch?.();
  };

  const onPick = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    if (pending) {
      // 單還不存在:先交給父層收著,建單成功後由父層補傳(REQ-20261012001)
      pending.onChange([...pending.files, file]);
      if (fileInput.current) fileInput.current.value = '';
      if (cameraInput.current) cameraInput.current.value = '';
      return;
    }
    if (docId == null) return;
    setError(null);
    setUploading(true);
    try {
      // 類型**不在這裡問** —— 先存檔,chip 上事後補(需求原文)
      await uploadDocumentAttachment(docType, docId, file);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : '上傳失敗');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
      if (cameraInput.current) cameraInput.current.value = '';
    }
  };

  const onSetCategory = async (
    attachmentId: number,
    category: DocumentAttachmentCategory | null,
  ): Promise<void> => {
    if (docId == null) return;
    setError(null);
    try {
      await patchDocumentAttachmentCategory(docType, docId, attachmentId, category);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : '設定類型失敗');
    }
  };

  const onRemove = async (att: DocumentAttachmentResponse): Promise<void> => {
    if (docId == null) return;
    // 二次確認沿用附件區既有的做法(REQ-20260819007),不自創新彈窗
    if (!window.confirm(`刪除「${att.originalName}」？`)) return;
    setError(null);
    try {
      await deleteDocumentAttachment(docType, docId, att.id);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : '刪除失敗');
    }
  };

  /**
   * 回存檢視角度(REQ-20260909004)。權限用既有的 `canEdit`(凍結後附件唯讀,
   * R-12 + R-08)—— 後端的 `setRotation()` 也是同一條判定,兩邊一致。
   * 錯誤不在這裡接:lightbox 自己有錯誤區塊,丟回去讓它就地顯示。
   */
  const onSaveRotation = async (
    image: LightboxImage,
    rotation: number,
  ): Promise<void> => {
    const found = imageAttachments.find((a) => a.uuid === image.uuid);
    if (!found || docId == null) throw new Error('找不到附件');
    await saveDocumentAttachmentRotation(
      docType,
      docId,
      found.id,
      rotation as AttachmentRotation,
    );
    refresh();
  };

  return (
    <fieldset className="grid gap-2 border rounded-md p-3" data-testid={`${testIdPrefix}-card`}>
      {/* legend 要與右邊那行小字併成一列就得包一層 div —— `<legend>` 是 fieldset 的
          直屬子節點時排不進 flex(沿用 REQ-20260905006 立下的做法) */}
      <div className="flex items-baseline justify-between gap-2">
        <legend className="text-sm font-medium text-slate-700">{title}</legend>
        <span className="text-[11px] text-slate-400" data-testid={`${testIdPrefix}-count`}>
          {attachments.length + (pending?.files.length ?? 0)} 個檔案
        </span>
      </div>
      <p className="text-[11px] text-slate-400 -mt-1">{subtitle}</p>

      <div className="grid gap-2 md:grid-cols-3 items-start">
        {attachments.map((att) => (
          <div
            key={att.id}
            className="relative flex items-start gap-2 border rounded-md p-2"
            data-testid={`document-attachment-${att.id}`}
          >
            <AttachmentThumbnail
              docType={docType}
              docId={docId}
              attachment={att}
              onOpenImage={() =>
                setLightboxIndex(lightboxImages.findIndex((img) => img.uuid === att.uuid))
              }
            />
            <div className="grid gap-1 min-w-0 flex-1 pr-4">
              <CategoryChip
                attachment={att}
                canEdit={canEdit}
                onChange={(category) => void onSetCategory(att.id, category)}
              />
              <span className="text-[11px] text-slate-400">
                {formatUploadedAt(att.uploadedAt)} 上傳
              </span>
              <span className="text-[11px] text-slate-400 truncate" title={att.originalName}>
                {att.originalName}
              </span>
            </div>
            {canEdit ? (
              <button
                type="button"
                aria-label={`刪除 ${att.originalName}`}
                onClick={() => void onRemove(att)}
                className="absolute right-1 top-1 text-slate-400 hover:text-red-600 text-xs leading-none p-1"
              >
                ✕
              </button>
            ) : null}
          </div>
        ))}

        {/* 建單模式的暫存檔(REQ-20261012001)。還沒進系統 → 不做縮圖、類型 chip、
            lightbox(類型照既有規則在上傳後補);✕ 只是從暫存清單拿掉,
            沒有東西會從系統裡被刪,所以不走 window.confirm。
            ✕ 與上方既有附件一樣只在 canEdit 時出現(父層建單 / 補傳中會傳 disabledReason)*/}
        {pending?.files.map((file, i) => (
          <div
            key={`${i}-${file.name}`}
            className="relative flex items-start gap-2 border border-dashed rounded-md p-2"
            data-testid={`${testIdPrefix}-pending-${i}`}
          >
            <span className="shrink-0 w-14 h-14 bg-slate-100 rounded flex items-center justify-center">
              <FileIcon />
            </span>
            <div className="grid gap-1 min-w-0 flex-1 pr-4">
              <span className="justify-self-start rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700">
                待上傳
              </span>
              <span className="text-[11px] text-slate-400 truncate" title={file.name}>
                {file.name}
              </span>
            </div>
            {canEdit ? (
              <button
                type="button"
                aria-label={`移除 ${file.name}`}
                onClick={() => pending.onChange(pending.files.filter((_, j) => j !== i))}
                className="absolute right-1 top-1 text-slate-400 hover:text-red-600 text-xs leading-none p-1"
              >
                ✕
              </button>
            ) : null}
          </div>
        ))}

        {/* 上傳區。**永遠渲染** —— 不能編輯時停用並說明原因,而不是整格消失 */}
        <div className="grid gap-1 border border-dashed rounded-md p-3 justify-items-center text-sm">
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!canEdit || uploading}
              onClick={() => cameraInput.current?.click()}
              className="text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
            >
              拍照
            </button>
            <span className="text-slate-300">|</span>
            <button
              type="button"
              disabled={!canEdit || uploading}
              onClick={() => fileInput.current?.click()}
              className="text-blue-600 hover:underline disabled:text-slate-400 disabled:no-underline"
            >
              選檔案
            </button>
          </div>
          {reason ? <span className="text-[11px] text-slate-400">{reason}</span> : null}
          {pending ? <span className="text-[11px] text-slate-400">建單時一併上傳</span> : null}
          {uploading ? (
            <span role="status" className="text-[11px] text-slate-500">
              上傳中...
            </span>
          ) : null}
          {/*
            兩個 input 都藏起來,用上面的按鈕觸發:「拍照」多帶 `capture` 讓手機
            直接開相機。桌機瀏覽器會忽略 `capture`,退化成一般選檔 —— 不會壞掉。
          */}
          <input
            ref={cameraInput}
            type="file"
            accept={DOCUMENT_ATTACHMENT_ACCEPT}
            capture="environment"
            className="hidden"
            data-testid={`${testIdPrefix}-camera-input`}
            onChange={(e) => void onPick(e.target.files?.[0])}
          />
          <input
            ref={fileInput}
            type="file"
            accept={DOCUMENT_ATTACHMENT_ACCEPT}
            className="hidden"
            data-testid={`${testIdPrefix}-file-input`}
            onChange={(e) => void onPick(e.target.files?.[0])}
          />
        </div>
      </div>

      {error ? (
        <div role="alert" className="text-red-600 text-sm">
          {error}
        </div>
      ) : null}

      <p className="text-[11px] text-slate-400">
        支援圖片與 PDF。上傳後可指定類型,類型會影響報表和查詢的分類。
      </p>

      {lightboxIndex !== null && lightboxIndex >= 0 ? (
        <ImageLightbox
          images={lightboxImages}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          resolveUrl={resolveAttachmentUrl}
          {...(canEdit ? { onSaveRotation } : {})}
        />
      ) : null}
    </fieldset>
  );
}

/**
 * 類型 chip。未指定時顯示「未分類」,點下去展開五種類型 + 一個「清除」。
 * **不是下拉選單**:它同時是「目前是什麼」的標示與「改成什麼」的入口,
 * 而 `<select>` 在只有幾個值時反而多一次點擊(而且清除要另外做一個空選項)。
 */
function CategoryChip({
  attachment,
  canEdit,
  onChange,
}: {
  attachment: DocumentAttachmentResponse;
  canEdit: boolean;
  onChange: (category: DocumentAttachmentCategory | null) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const label =
    attachment.category != null
      ? (DOCUMENT_ATTACHMENT_CATEGORIES[
          attachment.category as DocumentAttachmentCategory
        ] ?? attachment.category)
      : '未分類';

  if (!canEdit) {
    return (
      <span className="justify-self-start rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600">
        {label}
      </span>
    );
  }

  return (
    <div className="grid gap-1 justify-items-start">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`document-attachment-category-${attachment.id}`}
        className={`rounded px-1.5 py-0.5 text-[11px] ${
          attachment.category != null
            ? 'bg-blue-50 text-blue-700'
            : 'bg-slate-100 text-slate-500'
        }`}
      >
        {label}
      </button>
      {open ? (
        <div className="flex flex-wrap gap-1">
          {DOCUMENT_ATTACHMENT_CATEGORY_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                onChange(key);
                setOpen(false);
              }}
              className="rounded border px-1.5 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50"
            >
              {DOCUMENT_ATTACHMENT_CATEGORIES[key]}
            </button>
          ))}
          {attachment.category != null ? (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="rounded border px-1.5 py-0.5 text-[11px] text-slate-400 hover:bg-slate-50"
            >
              清除
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 縮圖。圖片走 signed URL 直接當 `<img src>`,PDF 走 pdf.js 畫第一頁,
 * 其餘(以及任何一步失敗)退回檔案圖示。
 *
 * ⚠️ signed URL 5 分鐘過期(`staleTime` 4 分鐘,同既有做法)。表單開很久沒動時
 * 縮圖可能失效,重整就會再拿一次。
 */
function AttachmentThumbnail({
  docType,
  docId,
  attachment,
  onOpenImage,
}: {
  docType: DocumentType;
  docId: number | undefined;
  attachment: DocumentAttachmentResponse;
  onOpenImage: () => void;
}): JSX.Element {
  const urlQuery = useDocumentAttachmentUrl(docType, docId, attachment.id);
  const isImage = attachment.mimeType.startsWith('image/');
  const isPdf = attachment.mimeType === 'application/pdf';

  const box = 'shrink-0 w-14 h-14 bg-slate-100 rounded flex items-center justify-center overflow-hidden';

  if (urlQuery.isError || !urlQuery.data) {
    return (
      <span className={box}>
        <FileIcon />
      </span>
    );
  }

  if (isImage) {
    return (
      <span className={box}>
        <button
          type="button"
          onClick={onOpenImage}
          className="block focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {/*
            縮圖照已存的角度顯示(REQ-20260909004),不然卡片上那張還是躺的。
            縮圖框是 56×56 的正方形而 img 剛好填滿它,轉 90/270 度後外框仍是同一個
            正方形 —— 不需要 ImageLightbox 那段 maxWidth/maxHeight 對調(那是給
            max-w-[90vw]/max-h-[85vh] 這種長寬不等的框用的),外層也還有 overflow-hidden。
          */}
          <img
            src={urlQuery.data}
            alt={attachment.originalName}
            className="w-14 h-14 object-cover"
            style={{ transform: `rotate(${attachment.rotation}deg)` }}
          />
        </button>
      </span>
    );
  }

  if (isPdf) {
    return (
      <span className={box}>
        <PdfThumbnail url={urlQuery.data} />
      </span>
    );
  }

  return (
    <span className={box}>
      <FileIcon />
    </span>
  );
}

/**
 * PDF 第一頁的縮圖(REQ-20260907002)。
 *
 * ⚠️ 這**推翻了 REQ-20260901010 §2.5「不做 PDF 縮圖」的結論** —— 那次的理由是
 * 「需求只要看得到內容,新分頁開原檔就夠」,而這次使用者明確指定「PDF 縮圖顯示
 * 第一頁」。是需求變了,不是當初漏看。代價(多一個相依 `pdfjs-dist`)照收。
 *
 * `pdfjs-dist` 與它的 worker 都用**動態 import**:
 *  - 沒有 PDF 附件的畫面一個 byte 都不會載到它(它不小)。
 *  - 測試環境(jsdom 沒有 canvas)不會在 import 期就炸掉。
 *
 * **任何一步失敗都退回檔案圖示**,絕不讓表單壞掉:抓不到檔、pdf.js 載不起來、
 * 瀏覽器沒有 canvas、頁面畫不出來 —— 全部走同一條 fallback。
 */
function PdfThumbnail({ url }: { url: string }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const pdfjs = await import('pdfjs-dist');
        const workerUrl = (await import('pdfjs-dist/build/pdf.worker.mjs?url')).default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

        const doc = await pdfjs.getDocument({ url }).promise;
        const page = await doc.getPage(1);
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          setFailed(true);
          return;
        }
        // 縮圖框是 56×56(w-14 h-14),取能塞滿寬度的比例即可
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: 56 / base.width });
        canvas.width = Math.max(1, Math.floor(viewport.width));
        canvas.height = Math.max(1, Math.floor(viewport.height));
        await page.render({ canvas, canvasContext: ctx, viewport }).promise;
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (failed) return <FileIcon />;
  return <canvas ref={canvasRef} className="w-14 h-14 object-cover" data-testid="pdf-thumbnail" />;
}

/** 檔案圖示。與附件區既有的迴紋針同一種寫法(inline SVG + currentColor) */
function FileIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6 text-slate-400"
      data-testid="document-attachment-file-icon"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
    </svg>
  );
}

/**
 * 「MM／DD」。**明確指定 Asia/Taipei** —— 直接 `slice()` ISO 字串取到的是 UTC 日期,
 * 台灣時間 00:00-08:00 上傳的檔案會顯示成前一天(CLAUDE.md §6.1)。
 */
export function formatUploadedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    month: '2-digit',
    day: '2-digit',
  })
    .format(d)
    .replace('-', '/');
}
